import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface RefundModalProps {
  open: boolean;
  onClose: () => void;
  onRefund: (chargeId: string, amount?: number, reason?: string) => Promise<void>;
  chargeId: string;
  maxAmount: number;
  currency: string;
}

export const RefundModal: React.FC<RefundModalProps> = ({ open, onClose, onRefund, chargeId, maxAmount, currency }) => {
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('requested_by_customer');
  const [isPartial, setIsPartial] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const refundAmount = isPartial ? Math.round(parseFloat(amount) * 100) : undefined;
      
      if (isPartial && (!refundAmount || refundAmount <= 0 || refundAmount > maxAmount)) {
        throw new Error('Invalid refund amount');
      }

      await onRefund(chargeId, refundAmount, reason);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Refund failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-stone-200">
        <div className="px-6 py-4 border-b border-stone-100 flex justify-between items-center">
          <h3 className="font-bold text-stone-800">Process Refund</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-xl text-sm flex items-start gap-2 border border-red-200">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <div className="space-y-3">
            <label className="flex items-center gap-3 p-3 border border-stone-200 rounded-xl cursor-pointer hover:bg-stone-50 transition-colors">
              <input
                type="radio"
                name="refundType"
                checked={!isPartial}
                onChange={() => setIsPartial(false)}
                className="text-amber-500 focus:ring-amber-500"
              />
              <div>
                <div className="font-medium text-stone-800">Full Refund</div>
                <div className="text-sm text-stone-500">
                  Refund the full amount of {new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(maxAmount / 100)}
                </div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 border border-stone-200 rounded-xl cursor-pointer hover:bg-stone-50 transition-colors">
              <input
                type="radio"
                name="refundType"
                checked={isPartial}
                onChange={() => setIsPartial(true)}
                className="text-amber-500 focus:ring-amber-500"
              />
              <div>
                <div className="font-medium text-stone-800">Partial Refund</div>
                <div className="text-sm text-stone-500">Refund a specific amount</div>
              </div>
            </label>
          </div>

          {isPartial && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Amount ({currency.toUpperCase()})</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-stone-400">$</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="block w-full pl-7 pr-3 py-3 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 placeholder-stone-400 focus:ring-2 focus:ring-amber-500/50 focus:border-transparent"
                  placeholder="0.00"
                  max={maxAmount / 100}
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Reason</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="block w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 focus:ring-2 focus:ring-amber-500/50 focus:border-transparent"
            >
              <option value="requested_by_customer">Requested by customer</option>
              <option value="duplicate">Duplicate</option>
              <option value="fraudulent">Fraudulent</option>
            </select>
          </div>

          <div className="pt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-stone-700 bg-stone-100 hover:bg-stone-200 rounded-xl font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2.5 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2 transition-colors"
            >
              {loading ? 'Processing...' : 'Refund Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
